import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import StudyDescriptionEditor from 'studies/workspace/StudyDescriptionEditor';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import * as Colors from 'ui-library/Colors';
import {Tab, TabContainer, TabContainerProps, TabProps} from 'ui-library/TabContainer';

import {StudyOverviewTabContentsProps} from '../utils/TypedStudyTypes';

interface BaseStudyExecutionProps extends StudyOverviewTabContentsProps {
  dataTestAttrs?: DataTestAttrs;
  executionContainerStyling?: React.CSSProperties;
  headerBreadcrumb?: JSX.Element;
  headerButtons?: {buttonsStyling: React.CSSProperties; buttons: JSX.Element};
  headerSectionContainerStyling?: React.CSSProperties;
  procedureRunId?: string;
  title?: string;
}

/** @package */
export type StudyExecutionHeaderTabsProps<T extends string> =
  | {
      headerTabContainerProps: Omit<TabContainerProps<T>, 'children'>;
      headerTabsProps: Array<Omit<TabProps<T>, 'children'>>;
    }
  | {headerTabContainerProps?: never; headerTabsProps?: never};
type StudyExecutionProps<T extends string> = BaseStudyExecutionProps & StudyExecutionHeaderTabsProps<T>;

export default function StudyExecution<T extends string>(props: StudyExecutionProps<T>): JSX.Element {
  return (
    <div className={css(styles.studyExecutionContainer)} {...getTestProps(props.dataTestAttrs)}>
      <div style={props.headerSectionContainerStyling}>
        <div className={css(styles.headerSection)}>
          {props.headerBreadcrumb && <div className={css(styles.breadcrumbs)}>{props.headerBreadcrumb}</div>}
          <div className={css(styles.headerTitleAndButtons)}>
            <div className={css(styles.title)}>{props.title ?? props.study.name}</div>
            <div
              className={props.headerButtons ? undefined : css(styles.headerButtons)}
              style={props.headerButtons?.buttonsStyling}
            >
              {props.headerButtons?.buttons}
            </div>
          </div>
          <ArchiveRecordLabel
            item={{name: props.study.name, id: props.study.id, archiveRecord: props.study.archiveRecord}}
          />
          {!props.procedureRunId && (
            <div className={css(styles.description)}>
              <StudyDescriptionEditor
                study={props.study}
                refetch={props.refetch}
                isWritable={props.isEditable}
              />
            </div>
          )}
        </div>
      </div>
      {props.headerTabContainerProps && (
        <TabContainer {...props.headerTabContainerProps}>
          {props.headerTabsProps.map((headerTabProps) => (
            <Tab key={headerTabProps.tabKey} {...headerTabProps}>
              {props.execution}
            </Tab>
          ))}
        </TabContainer>
      )}
      {!props.headerTabContainerProps && (
        <div className={css(styles.execution)} style={props.executionContainerStyling}>
          {props.execution}
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  breadcrumbs: {
    marginBottom: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  headerTitleAndButtons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  studyExecutionContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    paddingTop: 12,
  },
  title: {
    fontSize: '26px',
    lineHeight: '26px',
    fontWeight: 'bolder',
    display: 'inline-block',
  },
  headerButtons: {
    flex: 'none',
    display: 'flex',
    marginLeft: 'auto',
    padding: 20,
    marginRight: '40px',
    gap: 18,
  },
  headerSection: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: Colors.WHITE,
    flex: '0 0 auto',
  },
  description: {
    marginBottom: 12,
  },
  execution: {
    backgroundColor: Color.GRAY_100,
    height: '100%',
    maxHeight: '100%',
  },
});
